import { useCombinedQueryStatus } from "../../core/queryHelpers";
import { useAccessLimitsQuery, useSharedLimitsMutation, useSharedLimitsQuery } from "../api/limitsApiHooks";
import { Children, useEffect, useMemo, useState } from 'react';
import { Alert, Box, Button, Heading, Text, useToasts } from 'tombac';
import { useUserDetails } from "../../users/UserDetailsProvider";
import { LimitsConfiguratorModal } from "./LimitsConfiguratorModal";
import { SharedLimitsSummary } from "./SharedLimitsSummary";
import { sharedLimitSchemas } from "./sharedLimitSchemas";
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
function useExistingProductLimitNames(productLimitQueries) {
  return useMemo(() => {
    const productLimits = productLimitQueries.reduce((combined, query) => Object.assign(combined, query.data?.limits), {});
    return sharedLimitSchemas.filter(_ref => {
      let {
        name
      } = _ref;
      return productLimits[name] !== null && productLimits[name] !== undefined;
    }).map(schema => schema.name);
  }, [...productLimitQueries, sharedLimitSchemas]);
}
export const ProductPackages = _ref2 => {
  let {
    children,
    hasUserContracts
  } = _ref2;
  const {
    blockOldAccessProcess,
    email: username
  } = useUserDetails();
  const {
    addToast
  } = useToasts();
  const sharedLimitsQuery = useSharedLimitsQuery();
  const sharedLimits = sharedLimitsQuery.data?.limits;
  const {
    mutateAsync: mutateSharedLimits
  } = useSharedLimitsMutation();
  const [isEditOpen, setIsEditOpen] = useState(false);
  useEffect(() => {
    sharedLimitsQuery.refetch(); // Make sure the data is fresh each time the modal is open/closed
  }, [isEditOpen]);
  const packagedProductsExist = Children.count(children);
  const productLimitQueries = [useAccessLimitsQuery({
    productId: 'od',
    username
  }), useAccessLimitsQuery({
    productId: 'ts',
    username
  })];
  const existingProductLimits = useExistingProductLimitNames(productLimitQueries);
  const [isOverwritingLimits, setIsOverwritingLimits] = useState(false);
  const handleChange = formikContext => {
    let overwriting = false;
    for (const existingName of existingProductLimits) {
      if (formikContext.values[existingName] !== null && formikContext.values[existingName] !== undefined) {
        overwriting = true;
      }
    }
    if (overwriting !== isOverwritingLimits) {
      setIsOverwritingLimits(overwriting);
    }
  };
  const handleSubmit = async limits => {
    try {
      await mutateSharedLimits({
        limits,
        username
      });
      setIsEditOpen(false);
    } catch (error) {
      console.error(error);
      addToast('Could not save shared limits.', 'danger', {
        content: 'Please try again later or contact MOVE support.'
      });
    }
  };
  const combinedQueryStatus = useCombinedQueryStatus(sharedLimitsQuery, ...productLimitQueries);
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsxs(Box, {
      $display: "flex",
      "data-sentry-element": "Box",
      "data-sentry-source-file": "ProductPackages.tsx",
      "data-sentry-element": "Box",
      children: [/*#__PURE__*/_jsx(Heading, {
        level: 3,
        "data-sentry-element": "Heading",
        "data-sentry-source-file": "ProductPackages.tsx",
        "data-sentry-element": "Heading",
        children: "Packages"
      }), !packagedProductsExist && !hasUserContracts && !blockOldAccessProcess && /*#__PURE__*/_jsx(Button, {
        $ml: "auto",
        onClick: () => setIsEditOpen(true),
        children: "Add TS + OD package"
      })]
    }), packagedProductsExist ? /*#__PURE__*/_jsxs("div", {
      className: "ProductList",
      children: [children, /*#__PURE__*/_jsx(SharedLimitsSummary, {
        onRequestEditOpen: () => setIsEditOpen(true)
      })]
    }) : /*#__PURE__*/_jsx(Text, {
      $mb: "3sp",
      children: "No package had been added."
    }), /*#__PURE__*/_jsx(LimitsConfiguratorModal, {
      append: isOverwritingLimits && /*#__PURE__*/_jsx(Alert, {
        title: "Changing existing limits",
        variant: "alert",
        children: "Any previously set limits will be overwritten."
      }),
      isLoading: combinedQueryStatus.isLoading,
      isOpen: isEditOpen,
      limits: sharedLimits ?? {},
      onChange: handleChange,
      onRequestClose: () => setIsEditOpen(false),
      onSubmit: handleSubmit,
      schemas: sharedLimitSchemas,
      "data-sentry-element": "LimitsConfiguratorModal",
      "data-sentry-source-file": "ProductPackages.tsx",
      "data-sentry-element": "LimitsConfiguratorModal"
    })]
  });
};